import React from 'react';
import { Link } from 'react-router-dom';
import {
  MdOutlineSchool,
  MdOutlinePeople,
  MdOutlineLightbulb,
  MdOutlineVerified,
  MdOutlineStar,
} from 'react-icons/md';
import SeoHead from '../components/SeoHead';

const MEETING_LINK = 'https://meetings-eu1.hubspot.com/claus-moeldrup';

const stats = [
  { figure: '4,65 / 5', label: 'Som underviser i den offentlige sektor' },
  { figure: '93 %', label: 'af deltagere giver kurset 4 eller 5 stjerner' },
  { figure: '4,3 / 5', label: 'Samlet tilfredshed på tværs af forløb' },
  { figure: '100 %', label: 'NPS på egne kursusforløb' },
];

const reasons = [
  {
    Icon: MdOutlineVerified,
    title: 'Dokumenteret undervisningskvalitet',
    desc: 'Vores kurser er evalueret af hundredvis af deltagere på tværs af brancher og sektorer — med konsekvent høje scorer.',
  },
  {
    Icon: MdOutlineLightbulb,
    title: 'Praksis, ikke teori',
    desc: 'Vi lærer fra virkelige cases og konkrete eksempler. Alt hvad du lærer kan bruges i morgen — ikke om et år.',
  },
  {
    Icon: MdOutlineSchool,
    title: 'Eksperter med erfaring fra feltet',
    desc: 'Vi rådgiver og implementerer selv AI-løsninger for virksomheder. Det giver en undervisning der er forankret i virkeligheden.',
  },
  {
    Icon: MdOutlineStar,
    title: 'Tilpasset til jer',
    desc: 'Uanset om I er helt nye til AI eller allerede eksperimenterer, tilpasser vi indhold og tempo til præcis jeres udgangspunkt.',
  },
];

const agenda = [
  {
    Icon: MdOutlinePeople,
    step: '01',
    title: 'Kick-off: AI i jeres kontekst',
    desc: 'Vi etablerer et fælles udgangspunkt og sætter AI ind i en relevant ramme for netop jeres organisation og hverdag.',
  },
  {
    Icon: MdOutlineLightbulb,
    step: '02',
    title: 'Inspiration, cases og demonstrationer',
    desc: 'Konkrete eksempler på tværs af brancher og opgaver — se hvad AI reelt kan gøre, og hvad det ikke kan.',
  },
  {
    Icon: MdOutlineSchool,
    step: '03',
    title: 'Hands-on øvelser med prompts',
    desc: 'Deltagerne prøver selv — vi øver os i at formulere gode prompts og bruge AI på egne konkrete opgaver.',
  },
  {
    Icon: MdOutlineVerified,
    step: '04',
    title: 'Opsamling og næste skridt',
    desc: 'Vi samler op og giver anbefalinger til hvad jeres organisation med fordel kan gøre videre.',
  },
];

const AIKursus = () => {
  return (
    <div style={pageStyle}>
      <SeoHead
        title="AI-kursus | AICE"
        description="Lær AI af eksperter med dokumenterede resultater. AICE tilbyder AI-kursus for teams — praksisnært, konkret og tilpasset jeres udgangspunkt."
        canonicalPath="/ydelser/ai-kursus"
      />

      {/* HERO */}
      <section style={heroSection}>
        <div style={heroInner}>
          <p style={eyebrow}>Ydelser</p>
          <h1 style={heroTitle}>Lær AI af dem der arbejder med det hver dag</h1>
          <p style={heroText}>
            Et praksisnært 2-timers kursus der giver hele teamet fælles sprog,
            konkrete eksempler og hands-on erfaring. Usikker på AI? Det er præcis
            derfor du skal tale med os.
          </p>
          <div style={ctaRow}>
            <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <button type="button" style={primaryCta}>Book uforpligtende samtale</button>
            </a>
            <Link to="/ydelser" style={{ textDecoration: 'none' }}>
              <button type="button" style={secondaryCta}>Se alle ydelser</button>
            </Link>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section style={statsBar}>
        {stats.map(({ figure, label }) => (
          <div key={label} style={statItem}>
            <span style={statFigure}>{figure}</span>
            <span style={statLabel}>{label}</span>
          </div>
        ))}
      </section>

      {/* AGENDA */}
      <section style={section}>
        <div style={sectionHeader}>
          <p style={eyebrowSmall}>Workshopformat</p>
          <h2 style={sectionTitle}>Hvad sker der på de 2 timer?</h2>
          <p style={sectionSubtext}>
            Struktureret, men aldrig stift. Vi ved hvad vi skal ud af det — og vi når det.
          </p>
        </div>
        <div style={agendaGrid}>
          {agenda.map(({ Icon, step, title, desc }) => (
            <div key={step} style={agendaCard}>
              <div style={agendaTop}>
                <span style={agendaStep}>{step}</span>
                <Icon style={agendaIcon} />
              </div>
              <h3 style={agendaTitle}>{title}</h3>
              <p style={agendaDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section style={whySection}>
        <div style={sectionHeader}>
          <p style={eyebrowSmall}>Hvorfor AICE</p>
          <h2 style={sectionTitle}>AI-eksperter der underviser fra virkeligheden</h2>
        </div>
        <div style={reasonsGrid}>
          {reasons.map(({ Icon, title, desc }) => (
            <div key={title} style={reasonCard}>
              <Icon style={reasonIcon} />
              <h3 style={reasonTitle}>{title}</h3>
              <p style={reasonDesc}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FOOTER */}
      <section style={ctaSection}>
        <h2 style={ctaTitle}>Usikker på AI?</h2>
        <p style={ctaText}>
          Det er præcis det rigtige tidspunkt at tage en snak. Vi finder ud af hvad der
          giver mening for jer — helt uforpligtende.
        </p>
        <a href={MEETING_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
          <button type="button" style={primaryCta}>Book samtale</button>
        </a>
      </section>
    </div>
  );
};

export default AIKursus;

// ── Styles ──────────────────────────────────────────────

const pageStyle = {
  background: 'linear-gradient(180deg, #FFF4F0 0%, #FFFFFF 60%)',
  minHeight: '100vh',
};

const heroSection = {
  padding: '5rem clamp(1.5rem, 6vw, 6rem) 3rem',
  maxWidth: '900px',
  margin: '0 auto',
  textAlign: 'center',
};

const heroInner = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1.1rem',
};

const eyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: '#C03626',
  fontSize: '0.85rem',
  margin: 0,
};

const eyebrowSmall = {
  ...eyebrow,
  marginBottom: '0.4rem',
};

const heroTitle = {
  fontSize: 'clamp(2rem, 4vw, 3.2rem)',
  fontWeight: 800,
  color: '#1f1f1f',
  margin: 0,
  lineHeight: 1.15,
};

const heroText = {
  fontSize: '1.15rem',
  color: '#5C392F',
  lineHeight: 1.65,
  maxWidth: '600px',
  margin: 0,
};

const ctaRow = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginTop: '0.5rem',
};

const primaryCta = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  padding: '0.85rem 1.75rem',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
  boxShadow: '0 10px 24px rgba(192, 54, 38, 0.25)',
};

const secondaryCta = {
  background: '#fff',
  color: '#C03626',
  border: '1px solid #C03626',
  borderRadius: '10px',
  padding: '0.85rem 1.75rem',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: 'pointer',
};

const statsBar = {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  maxWidth: '900px',
  margin: '0 auto 4rem',
  borderRadius: '16px',
  border: '1px solid #F1D5CF',
  background: '#fff',
  boxShadow: '0 8px 24px rgba(192,54,38,0.07)',
  overflow: 'hidden',
};

const statItem = {
  flex: '1 1 180px',
  padding: '1.5rem 1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.3rem',
  borderRight: '1px solid #F1D5CF',
  textAlign: 'center',
};

const statFigure = {
  fontSize: '1.7rem',
  fontWeight: 800,
  color: '#C03626',
  lineHeight: 1,
};

const statLabel = {
  fontSize: '0.82rem',
  color: '#5C392F',
  lineHeight: 1.4,
};

const section = {
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '3rem clamp(1rem, 4vw, 3rem)',
};

const sectionHeader = {
  marginBottom: '2rem',
};

const sectionTitle = {
  fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: '0 0 0.5rem',
};

const sectionSubtext = {
  fontSize: '1.05rem',
  color: '#5C392F',
  lineHeight: 1.6,
  margin: 0,
  maxWidth: '600px',
};

const agendaGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.25rem',
};

const agendaCard = {
  background: '#fff',
  borderRadius: '16px',
  padding: '1.5rem',
  border: '1px solid #F1D5CF',
  boxShadow: '0 6px 18px rgba(192,54,38,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
};

const agendaTop = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const agendaStep = {
  fontSize: '1.6rem',
  fontWeight: 800,
  color: '#FFD6CC',
  lineHeight: 1,
};

const agendaIcon = {
  fontSize: '1.4rem',
  color: '#C03626',
  opacity: 0.7,
};

const agendaTitle = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const agendaDesc = {
  fontSize: '0.92rem',
  color: '#5C392F',
  lineHeight: 1.6,
  margin: 0,
};

const whySection = {
  maxWidth: '1000px',
  margin: '0 auto',
  padding: '1rem clamp(1rem, 4vw, 3rem) 3rem',
};

const reasonsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '1.5rem',
};

const reasonCard = {
  background: '#fff',
  borderRadius: '16px',
  padding: '1.5rem',
  border: '1px solid #F1D5CF',
  boxShadow: '0 8px 20px rgba(192,54,38,0.06)',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
};

const reasonIcon = {
  fontSize: '1.6rem',
  color: '#C03626',
};

const reasonTitle = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const reasonDesc = {
  fontSize: '0.95rem',
  color: '#5C392F',
  lineHeight: 1.6,
  margin: 0,
};

const ctaSection = {
  background: 'linear-gradient(135deg, #FFF4F0, #FFEAE4)',
  borderTop: '1px solid #F1D5CF',
  padding: '4rem clamp(1.5rem, 6vw, 6rem)',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
};

const ctaTitle = {
  fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
  fontWeight: 700,
  color: '#1f1f1f',
  margin: 0,
};

const ctaText = {
  fontSize: '1.05rem',
  color: '#5C392F',
  maxWidth: '500px',
  lineHeight: 1.65,
  margin: 0,
};
